import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BsSearch, BsFilter } from "react-icons/bs";
import FirestoreService from '../assets/js/firestore_instance';
import { doc, getDoc } from "firebase/firestore";

// Create a cache outside the component to persist between renders
const peopleCache = {
  data: null,
  groupsHash: null,
  timestamp: null,
  totalCount: 0
};

// Cache expiration time (10 minutes)
const CACHE_EXPIRATION = 10 * 60 * 1000;

const PeopleSection = ({ organization, groups = [] }) => {
  const [people, setPeople] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterOptions, setFilterOptions] = useState({
    showFilterMenu: false,
    selectedGroups: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [totalPeopleCount, setTotalPeopleCount] = useState(0);
  const activeGroups = groups.filter(group => group.joltActivated);

  // Generate a simple hash of the groups to detect changes
  const generateGroupsHash = (groups) => {
    return groups.map(g => `${g.id}:${g.members?.length || 0}`).join('|');
  };

  const processAvatarUrl = (url) => {
    if (!url) return null;
    
    // Check if the URL contains Firebase Storage pattern
    if (url.includes('firebasestorage.googleapis.com') && url.includes('alt=media')) {
      // Find the position to inject the thumbnail suffix
      const mediaParamIndex = url.indexOf('?alt=media');
      if (mediaParamIndex !== -1) {
        // Insert the _150x150 suffix before the query parameters
        return url.slice(0, mediaParamIndex) + '_150x150' + url.slice(mediaParamIndex);
      }
    }
    
    return url;
  };

  // Extract all unique user IDs from groups and fetch their details
  useEffect(() => {
    const currentGroupsHash = generateGroupsHash(groups);
    const cacheIsValid = 
      peopleCache.data && 
      peopleCache.groupsHash === currentGroupsHash &&
      (Date.now() - peopleCache.timestamp) < CACHE_EXPIRATION;
    
    const fetchUserDetails = async () => {
      if (groups.length === 0) {
        setIsLoading(false);
        setPeople([]);
        setTotalPeopleCount(0);
        return;
      }

      // Use cache if valid
      if (cacheIsValid) {
        console.log("Using cached people data");
        setPeople(peopleCache.data);
        setTotalPeopleCount(peopleCache.totalCount);
        setIsLoading(false);
        return;
      }

      // Otherwise fetch from database
      try {
        setIsLoading(true);
        const db = FirestoreService.getFirestore();
        const userIds = new Set();
        const userGroupMap = new Map();

        // Collect all unique user IDs and track which groups they belong to
        groups.forEach(group => {
          // Get the correct group ID
          const groupId = group.jolt_group_id || group.pco_id || group.id;
          
          if (group.members && Array.isArray(group.members)) {
            group.members.forEach(userId => {
              if (typeof userId === 'string') {
                userIds.add(userId);
                
                // Initialize or update the groups this user belongs to
                const groupInfo = { 
                  id: groupId,
                  title: group.title
                };
                
                if (!userGroupMap.has(userId)) {
                  userGroupMap.set(userId, [groupInfo]);
                } else {
                  userGroupMap.get(userId).push(groupInfo);
                }
              }
            });
          }
        });

        // Fetch user details for all collected user IDs
        const usersData = [];
        const userIdsArray = Array.from(userIds);
        const totalCount = userIdsArray.length;
        
        // Process users in batches to avoid large queries
        const batchSize = 10;
        for (let i = 0; i < userIdsArray.length; i += batchSize) {
          const batch = userIdsArray.slice(i, i + batchSize);
          
          // Fetch each user in parallel
          const userPromises = batch.map(async (userId) => {
            try {
              const userDoc = await getDoc(doc(db, "users", userId));
              
              if (userDoc.exists()) {
                // User exists in the database
                const userData = userDoc.data();

                return {
                  id: userId,
                  email: userData.email || `User: ${userId.substring(0, 8)}...`,
                  name: userData.displayName || userData.name || "Unknown User",
                  phone: userData.phoneNumber || userData.phone || null,
                  avatar: processAvatarUrl(userData.photourl) || userData.photourl || userData.avatar || null,
                  groups: userGroupMap.get(userId) || []
                };
              } else {
                // User doesn't exist in the database, create a placeholder
                return {
                  id: userId,
                  email: `User ID: ${userId.substring(0, 8)}...`,
                  name: "Unknown User",
                  phone: null,
                  avatar: null,
                  groups: userGroupMap.get(userId) || []
                };
              }
            } catch (error) {
              console.error(`Error fetching user ${userId}:`, error);
              // Return a placeholder for failed fetch
              return {
                id: userId,
                email: `User ID: ${userId.substring(0, 8)}...`,
                name: "User Fetch Failed",
                phone: null,
                avatar: null,
                groups: userGroupMap.get(userId) || []
              };
            }
          });
          
          const batchResults = await Promise.all(userPromises);
          usersData.push(...batchResults);
        }

        // Update the cache
        peopleCache.data = usersData;
        peopleCache.groupsHash = currentGroupsHash;
        peopleCache.timestamp = Date.now();
        peopleCache.totalCount = totalCount;

        // Update state
        setPeople(usersData);
        setTotalPeopleCount(totalCount);
      } catch (error) {
        console.error("Error fetching user details:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserDetails();
  }, [groups]);

  // Filter people based on search query and selected groups
  const filteredPeople = people.filter(person => {
    // Search filter
    const matchesSearch = 
      searchQuery === "" ||
      (person.name && person.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (person.email && person.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (person.phone && person.phone.includes(searchQuery));

    // Group filter
    const matchesGroup = 
      filterOptions.selectedGroups.length === 0 || 
      person.groups.some(group => filterOptions.selectedGroups.includes(group.id));

    return matchesSearch && matchesGroup;
  });

  const toggleGroupFilter = (groupId) => {
    setFilterOptions(prev => {
      const selectedGroups = [...prev.selectedGroups];
      const index = selectedGroups.indexOf(groupId);
      
      if (index === -1) {
        selectedGroups.push(groupId);
      } else {
        selectedGroups.splice(index, 1);
      }
      
      return { ...prev, selectedGroups };
    });
  };

  const clearAllFilters = () => {
    setSearchQuery("");
    setFilterOptions({
      showFilterMenu: false,
      selectedGroups: []
    });
  };

  const getGroupTagColor = (group) => {
    const colors = [
        "bg-orange-800 text-orange-100",
        "bg-blue-800 text-blue-100",
        "bg-green-800 text-green-100",
        "bg-purple-800 text-purple-100",
        "bg-rose-800 text-rose-100",
        "bg-teal-800 text-teal-100",
        "bg-amber-800 text-amber-100",
        "bg-indigo-800 text-indigo-100",
        "bg-sky-800 text-sky-100",
        "bg-emerald-800 text-emerald-100",
        "bg-violet-800 text-violet-100",
        "bg-fuchsia-800 text-fuchsia-100",
        "bg-pink-800 text-pink-100",
        "bg-cyan-800 text-cyan-100",
        "bg-lime-800 text-lime-100",
        "bg-yellow-800 text-yellow-100"
      ];
    
    const sum = group.title.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[sum % colors.length];
};

  return (
    <div className="dashboard-element-bg rounded-xl p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">People</h1>
          <div className="flex space-x-2">
            <div className="relative">
              <div className="flex items-center">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search people..."
                    className="pl-10 pr-4 py-2 bg-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none w-64"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <BsSearch className="absolute left-3 top-3 text-gray-400" />
                </div>
                <button
                  className="ml-2 px-3 py-2 bg-gray-700 rounded-lg hover:bg-gray-600 focus:outline-none relative"
                  onClick={() => setFilterOptions(prev => ({ ...prev, showFilterMenu: !prev.showFilterMenu }))}
                >
                  <BsFilter className="text-white text-xl" />
                  {filterOptions.selectedGroups.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {filterOptions.selectedGroups.length}
                    </span>
                  )}
                </button>
              </div>
              
              {/* Filter dropdown */}
              <AnimatePresence>
                {filterOptions.showFilterMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 mt-2 w-64 bg-gray-800 rounded-lg shadow-lg z-10 p-4"
                  >
                    <h3 className="text-white font-medium mb-2">Filter by Group</h3>
                    <div className="max-h-60 overflow-y-auto mb-3">
                      {groups.map(group => (
                        <div key={group.id} className="flex items-center mb-2">
                          <input
                            type="checkbox"
                            id={`group-${group.id}`}
                            checked={filterOptions.selectedGroups.includes(group.id)}
                            onChange={() => toggleGroupFilter(group.id)}
                            className="mr-2 rounded bg-gray-700 border-gray-600 text-orange-500 focus:ring-orange-500"
                          />
                          <label htmlFor={`group-${group.id}`} className="text-white cursor-pointer">
                            {group.title}
                          </label>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between">
                      <button
                        onClick={clearAllFilters}
                        className="text-sm text-gray-300 hover:text-white"
                      >
                        Clear All
                      </button>
                      <button
                        onClick={() => setFilterOptions(prev => ({ ...prev, showFilterMenu: false }))}
                        className="text-sm text-orange-400 hover:text-orange-300"
                      >
                        Apply
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Statistics */}
        {people.length > 0 && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-800 rounded-lg p-4 shadow-md">
              <h3 className="text-gray-300 text-sm mb-1">Total People</h3>
              <p className="text-2xl font-bold text-white">{totalPeopleCount}</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-4 shadow-md">
              <h3 className="text-gray-300 text-sm mb-1">Active Groups</h3>
              <p className="text-2xl font-bold text-white">{activeGroups.length}</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-4 shadow-md">
              <h3 className="text-gray-300 text-sm mb-1">Average Group Size</h3>
              <p className="text-2xl font-bold text-white">
                {activeGroups.length > 0 
                  ? Math.round(totalPeopleCount / activeGroups.length) 
                  : 0}
              </p>
            </div>
          </div>
        )}
        {/* People Table */}
        <div className="dashboard-on-element rounded-lg shadow-md overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500 mb-4"></div>
              <p>Loading people data...</p>
            </div>
          ) : filteredPeople.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-700">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Profile
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Email
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Phone
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Groups
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {filteredPeople.map((person, index) => (
                    <motion.tr 
                      key={person.email || index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ 
                        duration: 0.3,
                        delay: index * 0.05
                      }}
                      className="hover:bg-gray-700 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex-shrink-0 h-10 w-10">
                          {person.avatar ? (
                            <img 
                              className="h-10 w-10 rounded-full object-cover" 
                              src={person.avatar} 
                              alt={person.name || "User"} 
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-orange-700 flex items-center justify-center text-white font-medium">
                              {person.name ? person.name.charAt(0).toUpperCase() : "?"}
                            </div>
                          )}
                        </div>
                      </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-white max-w-[200px] truncate" title={person.name || "No name provided"}>
                        {person.name || "No name provided"}
                    </div>
                    </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-300">{person.email || "No email provided"}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-300">{person.phone || "No phone provided"}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1">
                        {person.groups.map((group, gIndex) => (
                            <span
                                key={gIndex}
                                className={`px-2 py-1 text-xs rounded-full ${getGroupTagColor(group)}`}
                            >
                                {group.title}
                            </span>
                            ))}
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292V12M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-300">No people found</h3>
              <p className="mt-1 text-sm text-gray-400">
                {groups.length === 0 
                  ? "Add members to your groups in JoltChat to see them here." 
                  : "Try adjusting your search or filters."}
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};
  
export default PeopleSection;